import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useContext, useEffect, useState } from 'react';
import { Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import Button from '../../../../components/Button';
import DefaultCard from '../../../../components/DefaultCard';
import IconButton from '../../../../components/IconButton/IconButton';
import Link from '../../../../components/Link';
import ModalHeader from '../../../../components/ModalHeader';
import { RadioGroup } from '../../../../components/Radio';
import SelectableCard from '../../../../components/SelectableCard';
import { EnrollmentStore } from '../../../../providers/enrollment';
import { getAllLongtermDisabilities } from '../../../../services/enrollment';

export type customLongTermDisabilityServicesModalProps = {
  showLongTermDisabilityServicesModal: boolean;
  handleCloseLongTermDisabilityServicesModal: () => void;
};

const LongTermDisabilityServicesModal = ({
  showLongTermDisabilityServicesModal,
  handleCloseLongTermDisabilityServicesModal,
}: customLongTermDisabilityServicesModalProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [longTermDisabilityServicesData, setLongTermDisabilityServicesData] =
    React.useState<any>({});

  const [radioButtonValue, setRadioButtonValue] = React.useState<
    string | number
  >(0);
  const [tellMeMore, setTellMeMore] = useState(false);
  const { activeLifeAndDisabilityInsurances, updateCost, updatePlanName } =
    useContext(EnrollmentStore);

  useEffect(() => {
    const data = getAllLongtermDisabilities();

    if (data) {
      setLongTermDisabilityServicesData(data);
    }
  }, [longTermDisabilityServicesData]);

  useEffect(() => {
    const currentBenefit = activeLifeAndDisabilityInsurances.find(
      (benefit: any) => benefit.type === 'long-term-disability'
    );
    const index = longTermDisabilityServicesData.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setRadioButtonValue(index);
  }, [activeLifeAndDisabilityInsurances, longTermDisabilityServicesData]);

  const handleRadioButtonValue = (e: string | number) => {
    setRadioButtonValue(e);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleTellMeMore = () => {
    setTellMeMore((prev) => !prev);
  };

  return (
    <>
      {longTermDisabilityServicesData ? (
        <Modal
          show={showLongTermDisabilityServicesModal}
          onHide={handleCloseLongTermDisabilityServicesModal}
          dialogClassName="modal-xl"
          className="custom-health-savings-modal"
        >
          <ModalHeader
            rightButton={
              <div onClick={toggleDetails} className={'icon-button-wrapper'}>
                <IconButton
                  variant="icon-default"
                  icon={light('circle-exclamation')}
                  extLabel="Benefit Info"
                />
              </div>
            }
            leftIcon={light('hospitals')}
            leftIconVariant="icon-default"
          >
            <h4 className="text-sm-20-lg-32">
              {longTermDisabilityServicesData['generic']
                ? longTermDisabilityServicesData['generic']['title']
                : ''}
            </h4>
          </ModalHeader>
          <ModalBody>
            <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
              <div className="d-flex justify-content-center">
                <DefaultCard
                  closeButton={
                    <Link 
                      className='pt-0' 
                      isAlignAtEnd 
                      isWhiteIcon 
                      rightIcon={light('close')}
                      onClick={toggleDetails}
                    />                      
                  }
                  variant="blue-motif"
                  leftHeading={
                    longTermDisabilityServicesData['generic']
                      ? longTermDisabilityServicesData['generic']['info'][
                          'title'
                        ]
                      : ''
                  }
                  leftSubheading={
                    <Container className={'px-0 d-flex flex-column'}>
                      {longTermDisabilityServicesData['generic'] ? (
                        <>
                          <span>
                            {
                              longTermDisabilityServicesData['generic']['info'][
                                'description'
                              ]
                            }
                          </span>
                          {tellMeMore && (
                            <span className={'mt-1'}>
                              {
                                longTermDisabilityServicesData['generic'][
                                  'info'
                                ]['additionalDescription']
                              }
                            </span>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </Container>
                  }
                  button={
                    <Button
                      onClick={handleTellMeMore}
                      variant="primary-shadow"
                      size="sm"
                    >
                      {tellMeMore ? 'Tell me less' : 'Tell me more'}
                    </Button>
                  }
                  className={'mb-12'}
                />
              </div>
            </Row>
            <Container className="p-0">
              <Row className="mb-7 mb-lg-12">
                <h5 className="text-sm-14-lg-24">
                  What option would you like?
                </h5>
              </Row>
              <Row>
                <RadioGroup
                  name={'pricing-test1'}
                  value={radioButtonValue}
                  onChange={handleRadioButtonValue}
                >
                  <div>
                    {longTermDisabilityServicesData['plans']
                      ? longTermDisabilityServicesData['plans'].map(
                          (
                            plan: {
                              title: string;
                              cost: any;
                              coverage: number;
                              subtitle1: string;
                            },
                            i: any
                          ) => (
                            <SelectableCard
                              title={plan.title}
                              prePaydPeriod={plan.cost}
                              radioButtonValue={i}
                              selected={radioButtonValue === i}
                              totalSum={plan.coverage ? plan.coverage : null}
                              disabled={
                                i ===
                                longTermDisabilityServicesData['plans'].length -
                                  1
                              }
                              key={i}
                            />
                          )
                        )
                      : ''}
                  </div>
                </RadioGroup>
              </Row>
            </Container>
          </ModalBody>
          <Modal.Footer className="pt-7">
            <Button
              onMouseDown={handleCloseLongTermDisabilityServicesModal}
              className="text-nowrap mt-7 mt-lg-0"
              variant="primary-outline"
            >
              Cancel
            </Button>
            <Button
              onMouseDown={() => {
                updateCost(
                  'long-term-disability',
                  longTermDisabilityServicesData.plans[radioButtonValue]
                );
                updatePlanName(
                  'long-term-disability',
                  longTermDisabilityServicesData.plans[radioButtonValue]
                );
                handleCloseLongTermDisabilityServicesModal();
              }}
              className="text-nowrap"
              variant="primary"
            >
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        'NO DATA'
      )}
    </>
  );
};

export default LongTermDisabilityServicesModal;
